import { PencilSquareIcon } from '@heroicons/react/16/solid';
import { ShareIcon } from '@heroicons/react/24/solid';
import { useMutationState } from '@tanstack/react-query';
import {
  createColumnHelper,
  getSortedRowModel,
  SortingState,
  useReactTable,
} from '@tanstack/react-table';
import { PropsWithChildren, useCallback, useMemo, useState } from 'react';
import { useLocalStorage } from 'usehooks-ts';
import { useAccount } from 'wagmi';
import { TRADE_PAGE_TABLE_ROWS, USD_DECIMALS } from '../../constants';
import { BUTTON_LABELS } from '../../constants/buttonLabels';
import { PORTFOLIO_PAGE, POSITION_STOP_ORDERS } from '../../constants/featureFlags';
import { PORTFOLIO_POSITIONS_ROUTE_URL } from '../../constants/routes';
import { useAccountEquity } from '../../hooks/useAccountEquity';
import { useFeatureFlag } from '../../hooks/useFeatureFlag';
import { useHandleExpiredSigner } from '../../hooks/useHandleExpiredSigner';
import { useModalState } from '../../hooks/useModalState';
import { getOrderMutationKey } from '../../hooks/useOrderMutation';
import { usePositionStopOrdersByProductId } from '../../hooks/useOrders';
import { usePositions } from '../../hooks/usePositions';
import { useProductPricesSub } from '../../hooks/useProductPricesSub';
import { ProductType, useProductsById } from '../../hooks/useProducts';
import { usePropsAsState } from '../../hooks/usePropAsState';
import { useActiveSelectedSubaccount } from '../../hooks/useSelectedSubaccount';
import { useTailwindBreakpoint } from '../../hooks/useTailwindBreakpoint';
import { cn } from '../../lib/classNameHelpers';
import {
  formatDollar,
  formatNumber,
  formatNumberWithDecimals,
  formatPercentage,
} from '../../lib/formatters';
import { Gwei, ZERO } from '../../lib/gwei';
import { localStorageKeys } from '../../lib/localstorageKeys';
import {
  calcDistanceToLiquidation,
  calcLiquidationPrice,
  calcMaintenanceMargin,
} from '../../lib/marginHelpers';
import { getDecimalPlaces, isEffectivelyZero, multiply } from '../../lib/math';
import { calcPnl, calcPnlPercentage } from '../../lib/positionHelpers';
import { sortNullableBigInt } from '../../lib/sortHelpers';
import { createDateRangeFilter, createProductIdFilter } from '../../lib/tableFilters';
import {
  positionFundingTooltip,
  positionMaintenanceMarginTooltip,
  positionPnlTooltip,
} from '../../lib/tooltips';
import { PrettyMerge } from '../../lib/tsUtilityTypes';
import { notNill } from '../../lib/typeGuards';
import { OrderSide } from '../../types/generated';
import { OrderType } from '../../types/orders';
import { Button } from '../catalyst-ui-kit/button';
import { ColoredMetric } from '../ColoredMetric/ColoredMetric';
import { DateRange } from '../DateRangeInput/DateRangeInput';
import { Loader } from '../Loader';
import { PositionStopOrderModal } from '../PositionStopOrderModal/PositionStopOrderModal';
import {
  CommonTableProps,
  getCommonTableParams,
  getRowLeftBorderClassName,
} from '../Table/commonTableParams';
import { getMarketColumn } from '../Table/getMarketColumn';
import { StateTable } from '../Table/Table';
import { TwoRowCell } from '../Table/TwoRowCell';
import { Tooltip } from '../Tooltip/Tooltip';

type TableData = {
  id: string;
  productId: string;
  ticker: string;
  displayTicker: string;
  side: OrderSide;
  size: bigint;
  avgPrice: bigint;
  oraclePrice: bigint | undefined;
  pnl: bigint | undefined;
  pnlPercentage: bigint | undefined;
  estLiqPrice: bigint | undefined;
  distanceToLiq: bigint | undefined;
  baseTokenName: string;
  maxLeverage: number | undefined;
  sizeDecimals: number;
  priceDecimals: number;
  fundingUsd: bigint;
  requiredMargin: bigint | undefined;
  tpPrice: bigint | undefined;
  slPrice: bigint | undefined;
};

type PositionCloseButtonProps = {
  productId: string;
  onCloseClick: (productId: string) => void;
  signerExpired: boolean;
};

const PositionCloseButton: React.FC<PropsWithChildren<PositionCloseButtonProps>> = ({
  productId,
  onCloseClick,
  signerExpired,
  children,
}) => {
  // Check if there's a pending mutation for this specific product
  const isClosing =
    useMutationState({
      filters: {
        mutationKey: getOrderMutationKey({
          productId,
          closing: true,
        }),
        status: 'pending',
      },
    }).length > 0;

  const handleClick = useCallback(() => {
    onCloseClick(productId);
  }, [onCloseClick, productId]);

  return (
    <Button size="2xs" text onClick={handleClick} disabled={signerExpired ? false : isClosing}>
      {children}
    </Button>
  );
};

type PositionsTableUiProps = PrettyMerge<
  {
    tableData: TableData[];
    signerExpired: boolean;
    closingAllPositions: boolean;
    onClosePositionClick: (productId: string, orderType: OrderType) => void;
    onCloseAllPositions: () => void;
    onEditStopOrderClick: (positionId: string) => void;
    onShowPnlCardModal: (positionId: string) => void;
    // Feature flags
    stopOrderEnabled: boolean;
    isConnected: boolean;
  },
  CommonTableProps
>;

export const PositionsTableUi: React.FC<PositionsTableUiProps> = ({
  tableData,
  signerExpired,
  closingAllPositions,
  onClosePositionClick,
  onCloseAllPositions,
  onEditStopOrderClick,
  stopOrderEnabled,
  onShowPnlCardModal,
  isConnected,
  // Common table props
  isFetching,
  isPending,
  isError,
  pagination,
  viewAllLink,
  pageSize,
  paginationKey,
  sorting,
  setSorting,
  fetchNextPage,
  setPageSize,
  pageSizeOptions,
  className,
  style,
}) => {
  const [hidePnl] = useLocalStorage(localStorageKeys.hidePnl, false);
  const isLg = useTailwindBreakpoint('lg');

  const columns = useMemo(() => {
    const columnHelper = createColumnHelper<TableData>();

    return [
      getMarketColumn(columnHelper, {
        displaySide: !isLg,
        enableSorting: true,
        // Will be enabled once we store leverage per position
        displayLeverage: false,
        meta: {
          accordion: {
            showInTrigger: true,
          },
        },
      }),
      columnHelper.accessor(
        ({ size, baseTokenName, oraclePrice, sizeDecimals }) => ({
          size,
          baseTokenName,
          oraclePrice,
          sizeDecimals,
        }),
        {
          id: 'size_baseTokenName_oraclePrice',
          header: 'Size',
          enableSorting: true,
          sortingFn: (a, b) => {
            const aNotional = a.original.oraclePrice
              ? Gwei.abs(multiply(a.original.size, a.original.oraclePrice))
              : 0n;
            const bNotional = b.original.oraclePrice
              ? Gwei.abs(multiply(b.original.size, b.original.oraclePrice))
              : 0n;
            return aNotional > bNotional ? 1 : aNotional < bNotional ? -1 : 0;
          },
          cell: (info) => {
            const { size, baseTokenName, oraclePrice, sizeDecimals } = info.getValue();
            return (
              <TwoRowCell
                primaryVal={`${formatNumber(Gwei.abs(size), { minimumFractionDigits: sizeDecimals, maximumFractionDigits: sizeDecimals })} ${baseTokenName}`}
                secondaryVal={formatDollar(
                  oraclePrice ? Gwei.abs(multiply(size, oraclePrice)) : undefined
                )}
              />
            );
          },
          meta: {
            accordion: {
              showInTrigger: true,
            },
          },
        }
      ),
      columnHelper.accessor(
        ({ avgPrice, priceDecimals }) => ({
          avgPrice,
          priceDecimals,
        }),
        {
          id: 'entry_mark',
          enableSorting: true,
          sortingFn: (a, b) => sortNullableBigInt(a.original.avgPrice, b.original.avgPrice),
          header: 'Entry Price',
          cell: (info) => {
            const { avgPrice, priceDecimals } = info.getValue();
            return formatNumber(avgPrice, {
              minimumFractionDigits: priceDecimals,
              maximumFractionDigits: priceDecimals,
            });
          },
        }
      ),
      columnHelper.accessor(
        ({ oraclePrice, priceDecimals }) => ({
          oraclePrice,

          priceDecimals,
        }),
        {
          id: 'oraclePrice',
          enableSorting: true,
          sortingFn: (a, b) => sortNullableBigInt(a.original.oraclePrice, b.original.oraclePrice),
          header: 'Oracle Price',
          cell: (info) => {
            const { oraclePrice, priceDecimals } = info.getValue();
            return formatNumber(oraclePrice, {
              minimumFractionDigits: priceDecimals,
              maximumFractionDigits: priceDecimals,
            });
          },
        }
      ),
      columnHelper.accessor(({ pnl, pnlPercentage, id }) => ({ pnl, pnlPercentage, id }), {
        id: 'pnl_pnlPercentage',
        enableSorting: true,
        sortingFn: (a, b) => sortNullableBigInt(a.original.pnl, b.original.pnl),
        header: () => (
          <Tooltip dottedUnderline={false} content={positionPnlTooltip()}>
            P&L
          </Tooltip>
        ),
        cell: (info) => {
          const { id, pnl, pnlPercentage } = info.getValue();

          return (
            <TwoRowCell
              primaryVal={
                <div className="relative">
                  {/* The actual PNL value that dictates width but is visually hidden when hidePnl is true */}
                  <div className={hidePnl ? 'invisible opacity-0' : ''}>
                    <ColoredMetric
                      value={pnl}
                      formatFn={(x) => formatDollar(x, { signDisplay: 'exceptZero' })}
                    />
                  </div>

                  {/* The asterisks that appear when hidePnl is true, positioned in the same space */}
                  {hidePnl && (
                    <div className="absolute inset-0 flex items-center justify-end">
                      <span>******</span>
                    </div>
                  )}
                </div>
              }
              secondaryVal={
                <ColoredMetric
                  value={pnlPercentage}
                  formatFn={(x) => formatPercentage(x, { signDisplay: 'exceptZero' })}
                />
              }
              icon={
                <Button
                  text
                  color="secondary"
                  onClick={(e) => {
                    e.stopPropagation();
                    onShowPnlCardModal(id);
                  }}
                >
                  <ShareIcon className="!size-3.5" />
                </Button>
              }
            />
          );
        },
        meta: {
          accordion: {
            showInTrigger: true,
          },
        },
      }),
      columnHelper.accessor(
        ({ estLiqPrice, priceDecimals }) => ({
          estLiqPrice,
          priceDecimals,
        }),
        {
          id: 'estLiqPrice',
          enableSorting: true,
          sortingFn: (a, b) => sortNullableBigInt(a.original.estLiqPrice, b.original.estLiqPrice),
          header: 'Est. Liq. Price',
          cell: (info) => {
            const { estLiqPrice, priceDecimals } = info.getValue();
            const formattedValue = formatNumber(estLiqPrice, {
              minimumFractionDigits: priceDecimals,
              maximumFractionDigits: priceDecimals,
            });

            if (isEffectivelyZero(estLiqPrice, priceDecimals)) {
              return (
                <Tooltip content={formatNumberWithDecimals(estLiqPrice)}>{formattedValue}</Tooltip>
              );
            }

            return formattedValue;
          },
        }
      ),
      columnHelper.accessor(({ fundingUsd, side }) => ({ fundingUsd, side }), {
        id: 'funding',
        enableSorting: true,
        sortingFn: (a, b) => sortNullableBigInt(a.original.fundingUsd, b.original.fundingUsd),
        header: () => (
          <Tooltip dottedUnderline={false} content={positionFundingTooltip()}>
            Funding
          </Tooltip>
        ),
        cell: (info) => {
          const { fundingUsd } = info.getValue();
          const formattedValue = formatDollar(fundingUsd, { signDisplay: 'negative' });
          const isZero = fundingUsd === ZERO || isEffectivelyZero(fundingUsd, USD_DECIMALS);

          return (
            <span
              className={cn({
                'text-contentRed': !isZero && fundingUsd < ZERO,
                'text-contentGreen': !isZero && fundingUsd > ZERO,
              })}
            >
              {formattedValue}
            </span>
          );
        },
      }),
      columnHelper.accessor('requiredMargin', {
        id: 'requiredMargin',
        enableSorting: true,
        sortingFn: (a, b) => {
          const aMargin = a.original.requiredMargin ?? 0n;
          const bMargin = b.original.requiredMargin ?? 0n;
          return aMargin > bMargin ? 1 : aMargin < bMargin ? -1 : 0;
        },
        header: () => (
          <Tooltip dottedUnderline={false} content={positionMaintenanceMarginTooltip()}>
            MM
          </Tooltip>
        ),
        cell: (info) => formatDollar(info.getValue()),
      }),
      stopOrderEnabled
        ? columnHelper.accessor(
            ({ tpPrice, slPrice, id, priceDecimals }) => ({ tpPrice, slPrice, id, priceDecimals }),
            {
              header: 'TP/SL',
              enableSorting: false,
              cell: (info) => {
                const { tpPrice, slPrice, id, priceDecimals } = info.getValue();
                return (
                  <div className="flex items-center gap-2">
                    <div className="flex flex-col text-3xs">
                      <div>
                        {formatNumber(tpPrice, {
                          maximumFractionDigits: priceDecimals,
                          minimumFractionDigits: priceDecimals,
                        })}
                      </div>
                      <div>
                        {formatNumber(slPrice, {
                          maximumFractionDigits: priceDecimals,
                          minimumFractionDigits: priceDecimals,
                        })}
                      </div>
                    </div>
                    {isLg && !signerExpired && (
                      <Button onClick={() => onEditStopOrderClick(id)} text color="secondary">
                        <PencilSquareIcon className="!size-3.5" />
                      </Button>
                    )}
                  </div>
                );
              },
            }
          )
        : undefined,
      columnHelper.accessor(({ id }) => ({ id }), {
        id: 'actions',
        enableSorting: false,
        header: () =>
          signerExpired || tableData.length === 0 ? (
            'Close'
          ) : (
            <Button
              text
              onClick={() => {
                onCloseAllPositions();
              }}
              disabled={closingAllPositions}
              className="flex items-center gap-0.5"
            >
              {closingAllPositions ? <Loader className="inline-block size-3" /> : null}
              <span className="text-3xs text-contentBlue">
                {closingAllPositions
                  ? BUTTON_LABELS.state.closingAll
                  : BUTTON_LABELS.action.closeAll}
              </span>
            </Button>
          ),
        cell: (info) => {
          const { id } = info.getValue();
          return (
            <div className="flex justify-end gap-4">
              <PositionCloseButton
                productId={info.row.original.productId}
                onCloseClick={() => onClosePositionClick(info.row.original.productId, 'LIMIT')}
                signerExpired={signerExpired}
              >
                {signerExpired ? BUTTON_LABELS.action.renewSigner : BUTTON_LABELS.action.limit}
              </PositionCloseButton>
              {!signerExpired && (
                <PositionCloseButton
                  productId={info.row.original.productId}
                  onCloseClick={() => onClosePositionClick(info.row.original.productId, 'MARKET')}
                  signerExpired={signerExpired}
                >
                  {BUTTON_LABELS.action.market}
                </PositionCloseButton>
              )}
              {stopOrderEnabled && !isLg && !signerExpired && (
                <Button size="2xs" text onClick={() => onEditStopOrderClick(id)}>
                  {BUTTON_LABELS.action.editTpSl}
                </Button>
              )}
            </div>
          );
        },
        meta: {
          align: 'right',
          accordion: {
            isActionColumn: true,
          },
        },
      }),
    ].filter(notNill);
  }, [
    isLg,
    stopOrderEnabled,
    hidePnl,
    onShowPnlCardModal,
    onEditStopOrderClick,
    signerExpired,
    tableData.length,
    closingAllPositions,
    onCloseAllPositions,
    onClosePositionClick,
  ]);

  const table = useReactTable({
    columns,
    data: tableData,
    meta: {
      getRowClassName: getRowLeftBorderClassName,
    },
    ...getCommonTableParams({
      pageSize,
      sorting,
      setSorting,
    }),
    getSortedRowModel: getSortedRowModel(),
    manualSorting: false,
  });

  return (
    <StateTable
      hasActions
      table={table}
      isPending={isPending}
      isConnected={isConnected}
      isFetching={isFetching}
      noDataText="Your open positions will appear here."
      viewAllLink={viewAllLink}
      showPaginationControls={pagination}
      fetchNextPage={fetchNextPage}
      paginationKey={paginationKey}
      isError={isError}
      pageSize={pageSize}
      setPageSize={setPageSize}
      pageSizeOptions={pageSizeOptions}
      className={className}
      style={style}
    />
  );
};

export const PositionsTable: React.FC<{
  products?: ProductType[];
  pagination?: boolean;
  side?: OrderSide;
  dateRange?: DateRange;
  defaultPageSize?: number;
  pageSizeOptions?: number[];
  className?: string;
  style?: React.CSSProperties;
}> = ({
  products,
  pagination,
  side,
  dateRange,
  defaultPageSize = TRADE_PAGE_TABLE_ROWS,
  pageSizeOptions = [defaultPageSize],
  className,
  style,
}) => {
  const { isConnected } = useAccount();
  const { signerExpired, withSignerCheck } = useHandleExpiredSigner();

  const { value: portfolioPageEnabled } = useFeatureFlag(PORTFOLIO_PAGE);
  const { value: stopOrderEnabled } = useFeatureFlag(POSITION_STOP_ORDERS);
  const selectedSubaccount = useActiveSelectedSubaccount();
  const firstProduct = products?.at(0);
  const productIdFilter = firstProduct ? firstProduct.id : undefined;

  const [sorting, setSorting] = useState<SortingState>([]);

  const [pageSize, setPageSize] = usePropsAsState(defaultPageSize);

  const {
    data: allPositions,
    isPending,
    isFetching,
    error,
  } = usePositions({
    subaccountId: selectedSubaccount?.id,
  });

  const positions = useMemo(() => {
    if (!allPositions) return [];

    const positionsWithSide = allPositions.map((position) => ({
      ...position,
      side: position.size > 0n ? OrderSide.BUY : OrderSide.SELL,
    }));

    const productIds =
      products && products?.length > 0 ? products.map((product) => product.id) : undefined;

    const filters = [
      createProductIdFilter<(typeof positionsWithSide)[0]>(productIds),
      createDateRangeFilter<(typeof positionsWithSide)[0]>(dateRange),
    ];

    const filtered = filters.reduce((acc, filter) => filter(acc), positionsWithSide);

    if (side !== undefined) {
      return filtered.filter((position) => position.side === side);
    }

    return filtered;
  }, [allPositions, products, side, dateRange]);

  const { data: stopOrdersByProductId } = usePositionStopOrdersByProductId({
    subaccountId: stopOrderEnabled ? selectedSubaccount?.id : undefined,
    productId: productIdFilter,
  });

  const productsById = useProductsById();
  const { data: pricesByProductId } = useProductPricesSub();
  const accountEquity = useAccountEquity();
  const closeAllPositionsModal = useModalState((state) => state.closeAllPositionsModal);
  const setPositionToCloseModal = useModalState((state) => state.setClosePositionModal);
  const setCloseAllPositionsModal = useModalState((state) => state.setCloseAllPositionsModal);
  const setPositionStopOrderModal = useModalState((state) => state.setPositionStopOrderModal);
  const setPnlCardModal = useModalState((state) => state.setPnlCardModal);

  const [showConfirmCloseAllModal] = useLocalStorage(
    localStorageKeys.showConfirmCloseAllModal,
    false
  );
  const closingAllPositions = showConfirmCloseAllModal && closeAllPositionsModal === 'open';

  const positionWithData = useMemo(
    () =>
      positions?.map((position) => ({
        ...position,
        product: productsById?.[position.productId],
        price: pricesByProductId?.[position.productId],
      })),
    [positions, pricesByProductId, productsById]
  );

  const tableData = useMemo(
    () =>
      positionWithData?.map((position) => {
        const product = position.product;

        const stopOrders = stopOrdersByProductId?.[position.productId];
        const price = position.price;
        const estLiqPrice =
          price?.oraclePrice && accountEquity && product?.maintenanceMarginRatio
            ? calcLiquidationPrice({
                accountEquity,
                positionSize: position.size,
                maintenanceMarginRatio: product.maintenanceMarginRatio,
                oraclePrice: price.oraclePrice,
              })
            : undefined;
        return {
          id: position.id,
          productId: position.productId,
          ticker: product?.ticker || '',
          displayTicker: product?.displayTicker || '',
          side: position.size > 0 ? OrderSide.BUY : OrderSide.SELL,
          size: position.size,
          avgPrice: position.avgPrice,
          oraclePrice: price?.oraclePrice,
          estLiqPrice,
          distanceToLiq:
            price?.oraclePrice && estLiqPrice
              ? calcDistanceToLiquidation({
                  oraclePrice: price.oraclePrice,
                  liquidationPrice: estLiqPrice,
                })
              : undefined,
          pnl: price?.oraclePrice
            ? calcPnl(price.oraclePrice, position.avgPrice, position.size)
            : undefined,
          pnlPercentage: price?.oraclePrice
            ? calcPnlPercentage(price.oraclePrice, position.avgPrice, position.size)
            : undefined,
          baseTokenName: product?.baseTokenName || '',
          maxLeverage: product?.maxLeverage,
          sizeDecimals: getDecimalPlaces(product?.lotSize || 0n),
          priceDecimals: getDecimalPlaces(product?.tickSize || 0n),
          fundingUsd: position.fundingUsd,
          requiredMargin:
            price?.oraclePrice && product
              ? calcMaintenanceMargin(
                  Gwei.abs(multiply(position.size, price?.oraclePrice)),
                  product.maxLeverage
                )
              : undefined,
          tpPrice: stopOrders?.takeProfit?.stopPrice,
          slPrice: stopOrders?.stopLoss?.stopPrice,
        } satisfies TableData;
      }),
    [accountEquity, positionWithData, stopOrdersByProductId]
  );

  const handleShowPnlCardModal = useCallback(
    (positionId: string) => {
      setPnlCardModal({ openOrClosed: 'open', positionId });
    },
    [setPnlCardModal]
  );
  const handleOnCloseClick = useCallback(
    (productId: string, orderType: OrderType) =>
      withSignerCheck(setPositionToCloseModal)({ openOrClosed: 'open', productId, orderType }),
    [withSignerCheck, setPositionToCloseModal]
  );
  const handleCloseAllPositions = useCallback(() => {
    setCloseAllPositionsModal('open');
  }, [setCloseAllPositionsModal]);
  const handleOnEditStopOrderClick = useCallback(
    (positionId: string) => {
      setPositionStopOrderModal({ openOrClosed: 'open', positionId });
    },
    [setPositionStopOrderModal]
  );

  return (
    <>
      <PositionsTableUi
        tableData={tableData || []}
        isPending={isPending}
        isFetching={isFetching}
        isError={!!error}
        pagination={pagination}
        signerExpired={signerExpired}
        closingAllPositions={closingAllPositions}
        onClosePositionClick={handleOnCloseClick}
        onCloseAllPositions={handleCloseAllPositions}
        onEditStopOrderClick={handleOnEditStopOrderClick}
        onShowPnlCardModal={handleShowPnlCardModal}
        stopOrderEnabled={stopOrderEnabled}
        isConnected={isConnected}
        sorting={sorting}
        setSorting={setSorting}
        pageSize={pageSize}
        setPageSize={setPageSize}
        pageSizeOptions={pagination ? pageSizeOptions : [pageSize]}
        fetchNextPage={() => {}}
        paginationKey={{ subaccountId: selectedSubaccount?.id, products, side, dateRange, sorting }}
        viewAllLink={
          portfolioPageEnabled && !pagination ? PORTFOLIO_POSITIONS_ROUTE_URL : undefined
        }
        className={className}
        style={style}
      />
      <PositionStopOrderModal />
    </>
  );
};
